import { useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, XCircle, CheckCircle2, Loader2, ScanLine, BookOpen, Search } from "lucide-react";

const SEV_TONE = {
  high: "text-destructive",
  medium: "text-[hsl(30_60%_40%)]",
  low: "text-muted-foreground",
};

export default function QualityPanel({ projectId, tree }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(null); // doc id currently AI-checking
  const [contradictions, setContradictions] = useState({}); // id -> {findings}

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/validate`);
      setReport(data);
      setContradictions({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const checkContradictions = async (docId) => {
    setChecking(docId);
    try {
      const { data } = await api.post(`/projects/${projectId}/documents/${docId}/contradictions`);
      setContradictions((c) => ({ ...c, [docId]: data.findings || [] }));
      if ((data.findings || []).length === 0) toast.success("No contradictions found");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChecking(null);
    }
  };

  const hasDocs = tree.length > 0;

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl font-medium tracking-tight">Quality &amp; validation</h2>
          <p className="mt-2 text-muted-foreground">
            Catch ungenerated, short, truncated or duplicate documents, check reading level, and run an
            AI contradiction check — before you publish.
          </p>
        </div>
        <Button onClick={run} disabled={loading || !hasDocs} className="rounded-sm" data-testid="run-validation-button">
          {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ScanLine size={16} className="mr-2" />}
          Run validation
        </Button>
      </div>

      {!report ? (
        <div className="rounded-sm border border-dashed border-border py-20 text-center text-muted-foreground" data-testid="quality-empty">
          {hasDocs ? "Run validation to inspect every document." : "Generate documents first, then validate."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat icon={ShieldCheck} label="Documents" value={report.total} tone="" />
            <Stat icon={CheckCircle2} label="Clean" value={report.clean} tone="text-[hsl(150_40%_35%)]" />
            <Stat icon={AlertTriangle} label="Warnings" value={report.warnings} tone="text-[hsl(30_60%_40%)]" />
            <Stat icon={XCircle} label="Errors" value={report.errors} tone="text-destructive" />
          </div>

          <div className="rounded-sm border border-border bg-card" data-testid="quality-report">
            {report.reports.map((r) => {
              const generated = r.status === "generated" || r.status === "ready" || (r.reading && r.word_count > 0);
              const found = contradictions[r.id];
              return (
                <div key={r.id} className="flex items-start gap-4 border-b border-border px-5 py-4 last:border-0">
                  <div className="mt-0.5">
                    {r.issues.length === 0 ? (
                      <CheckCircle2 size={18} className="text-[hsl(150_40%_35%)]" />
                    ) : r.issues.some((i) => i.level === "error") ? (
                      <XCircle size={18} className="text-destructive" />
                    ) : (
                      <AlertTriangle size={18} className="text-[hsl(30_60%_45%)]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{r.title}</span>
                      <span className="text-xs text-muted-foreground">{r.word_count} words</span>
                      {r.reading?.ease != null && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground"
                          data-testid={`reading-${r.id}`}
                          title={`Flesch reading ease ${r.reading.ease} · grade ~${r.reading.grade}`}
                        >
                          <BookOpen size={12} /> {r.reading.label}
                        </span>
                      )}
                    </div>
                    {r.issues.length === 0 ? (
                      <p className="mt-1 text-sm text-muted-foreground">No issues found.</p>
                    ) : (
                      <ul className="mt-2 space-y-1">
                        {r.issues.map((i, idx) => (
                          <li key={idx} className={`text-sm ${i.level === "error" ? "text-destructive" : "text-[hsl(30_50%_38%)]"}`}>
                            • {i.message}
                          </li>
                        ))}
                      </ul>
                    )}

                    {generated && (
                      <div className="mt-3">
                        {!found ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 rounded-sm text-xs"
                            onClick={() => checkContradictions(r.id)}
                            disabled={checking === r.id}
                            data-testid={`check-contradictions-${r.id}`}
                          >
                            {checking === r.id ? (
                              <Loader2 size={13} className="mr-1.5 animate-spin" />
                            ) : (
                              <Search size={13} className="mr-1.5" />
                            )}
                            {checking === r.id ? "Checking…" : "AI contradiction check"}
                          </Button>
                        ) : found.length === 0 ? (
                          <p className="text-xs text-[hsl(150_40%_35%)]" data-testid={`contradictions-clean-${r.id}`}>
                            ✓ No contradictions found by AI
                          </p>
                        ) : (
                          <div className="rounded-sm border border-border bg-secondary/40 p-3" data-testid={`contradictions-${r.id}`}>
                            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {found.length} possible contradiction{found.length === 1 ? "" : "s"}
                            </div>
                            <ul className="space-y-2">
                              {found.map((f, idx) => (
                                <li key={idx} className="text-sm">
                                  <span className={`font-medium ${SEV_TONE[f.severity] || ""}`}>[{f.severity}]</span>{" "}
                                  {f.issue}
                                  {f.quote && (
                                    <span className="mt-0.5 block text-xs italic text-muted-foreground">“{f.quote}”</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-sm border border-border bg-card p-5">
      <Icon size={18} className={tone || "text-muted-foreground"} />
      <div className={`mt-2 font-serif text-3xl font-semibold ${tone}`}>{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    </div>
  );
}
